import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  reminderLevelsByInvoiceId,
  type InvoiceReminderLevelSource,
} from '@/lib/invoiceReminderLevel';
import type { Database } from '@/types/database.generated';
import { appointmentRowIsInvoiceable } from '@/utils/calendarTimeBlock';

export type InvoiceRow = Database['public']['Tables']['invoices']['Row'];
export type InvoiceLineRow = Database['public']['Tables']['invoice_line_items']['Row'];
export type InvoicePaymentRow = Database['public']['Tables']['invoice_payments']['Row'];
export type InvoiceStatus = Database['public']['Enums']['invoice_status'];
export type InvoiceLineSource = Database['public']['Enums']['invoice_line_source'];
export type InvoiceAuditAction = Database['public']['Enums']['invoice_audit_action'];

export interface CreditNoteLink {
  id: string;
  invoice_number: string | null;
  total_cents: number;
}

export interface InvoiceListItem {
  id: string;
  clinic_id: string;
  patient_id: string;
  invoice_number: string | null;
  status: InvoiceStatus;
  kind?: string | null;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  vat_cents: number;
  total_cents: number;
  total_paid_cents: number;
  last_payment_at: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  last_sent_at: string | null;
  last_reminder_sent_at: string | null;
  last_emailed_to: string | null;
  created_at: string;
  updated_at: string;
  patient_first_name: string | null;
  patient_last_name: string | null;
  payment_method?: string | null;
}

export interface InvoiceDetail extends InvoiceRow {
  lines: InvoiceLineRow[];
}

export interface InvoiceListFilters {
  status?: InvoiceStatus | 'all';
  statuses?: string[];
  kind?: string;
  search?: string;
  patientIds?: string[];
  dateField?: 'issued_at' | 'due_at' | 'paid_at';
  dateFrom?: string;
  dateTo?: string;
  amountMinCents?: number;
  amountMaxCents?: number;
}

export const invoicesListQueryKey = (clinicId: string | null, filters: InvoiceListFilters) =>
  ['invoices', 'list', clinicId, filters] as const;

export const invoiceDetailQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'detail', invoiceId] as const;

function invalidateAppointmentInvoiceViews(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['appointments', 'invoice-link'] });
  qc.invalidateQueries({ queryKey: ['calendar', 'invoices'] });
  qc.invalidateQueries({ queryKey: ['appointments', 'patient'] });
}

export function useInvoicesList(clinicId: string | null | undefined, filters: InvoiceListFilters) {
  return useQuery({
    queryKey: invoicesListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<InvoiceListItem[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('invoices')
        .select(
          `id, clinic_id, patient_id, invoice_number, status, kind, currency,
           subtotal_cents, discount_cents, vat_cents, total_cents,
           total_paid_cents, last_payment_at, payment_method,
           issued_at, due_at, paid_at, voided_at, last_sent_at, last_reminder_sent_at, last_emailed_to,
           created_at, updated_at,
           patient:patients(first_name, last_name)`,
        )
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.kind) {
        q = q.eq('kind', filters.kind);
      }

      if (filters.statuses && filters.statuses.length > 0) {
        q = q.in('status', filters.statuses as InvoiceStatus[]);
      } else if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }

      if (filters.patientIds && filters.patientIds.length > 0) {
        q = q.in('patient_id', filters.patientIds);
      }

      if (filters.dateField && filters.dateFrom) {
        q = q.gte(filters.dateField, filters.dateFrom);
      }
      if (filters.dateField && filters.dateTo) {
        q = q.lte(filters.dateField, filters.dateTo);
      }

      if (filters.amountMinCents != null) {
        q = q.gte('total_cents', filters.amountMinCents);
      }
      if (filters.amountMaxCents != null) {
        q = q.lte('total_cents', filters.amountMaxCents);
      }

      const search = filters.search?.trim();
      if (search && /[0-9]/.test(search)) {
        // .or() splits on commas, so a search such as "1,0" is an invalid filter (HTTP 400).
        q = q.ilike('invoice_number', `%${search}%`);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row): InvoiceListItem => {
        const patient = (
          row as unknown as {
            patient: { first_name: string; last_name: string } | null;
          }
        ).patient;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          invoice_number: row.invoice_number,
          status: row.status,
          kind: row.kind,
          currency: row.currency,
          subtotal_cents: row.subtotal_cents,
          discount_cents: row.discount_cents,
          vat_cents: row.vat_cents,
          total_cents: row.total_cents,
          total_paid_cents: row.total_paid_cents ?? 0,
          last_payment_at: row.last_payment_at ?? null,
          issued_at: row.issued_at,
          due_at: row.due_at,
          paid_at: row.paid_at,
          voided_at: row.voided_at,
          last_sent_at: row.last_sent_at ?? null,
          last_reminder_sent_at: row.last_reminder_sent_at ?? null,
          last_emailed_to: row.last_emailed_to ?? null,
          created_at: row.created_at,
          updated_at: row.updated_at,
          patient_first_name: patient?.first_name ?? null,
          patient_last_name: patient?.last_name ?? null,
          payment_method: row.payment_method ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}

export function useInvoicePaymentAmounts(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', 'payment-amounts', clinicId] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<
      { invoice_id: string; amount_cents: number; payment_date: string | null }[]
    > => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('invoice_payments')
        .select('invoice_id, amount_cents, payment_date')
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        invoice_id: row.invoice_id,
        amount_cents: row.amount_cents,
        payment_date: row.payment_date,
      }));
    },
    staleTime: 30_000,
  });
}

export function useInvoicePaymentMethods(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', 'payment-methods', clinicId] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<{ methods: string[]; byInvoiceId: Map<string, string[]> }> => {
      if (!clinicId) return { methods: [], byInvoiceId: new Map() };

      const [invoices, payments] = await Promise.all([
        supabase
          .from('invoices')
          .select('id, payment_method')
          .eq('clinic_id', clinicId)
          .not('payment_method', 'is', null),
        supabase
          .from('invoice_payments')
          .select('invoice_id, payment_method')
          .eq('clinic_id', clinicId)
          .not('payment_method', 'is', null),
      ]);

      if (invoices.error) throw invoices.error;
      if (payments.error) throw payments.error;

      const byInvoiceId = new Map<string, string[]>();
      const unique = new Set<string>();

      const add = (invoiceId: string, method: string | null) => {
        const value = method?.trim();
        if (!value) return;
        unique.add(value);
        const current = byInvoiceId.get(invoiceId) ?? [];
        if (!current.includes(value)) byInvoiceId.set(invoiceId, [...current, value]);
      };

      for (const row of invoices.data ?? []) add(row.id, row.payment_method);
      for (const row of payments.data ?? []) add(row.invoice_id, row.payment_method);

      return { methods: [...unique].sort((a, b) => a.localeCompare(b, 'da')), byInvoiceId };
    },
    staleTime: 30_000,
  });
}

export function useInvoiceCreditNotes(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', 'credit-notes', invoiceId],
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<CreditNoteLink[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, total_cents, status, kind')
        .eq('credits_invoice_id', invoiceId)
        .eq('kind', 'credit_note');
      if (error) throw error;
      return (data ?? [])
        .filter((row) => row.status !== 'void' && row.status !== 'cancelled' && row.status !== 'draft')
        .map((row) => ({
          id: row.id,
          invoice_number: row.invoice_number,
          total_cents: row.total_cents,
        }));
    },
  });
}

export function useInvoice(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceDetailQueryKey(invoiceId ?? null),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceDetail> => {
      if (!invoiceId) throw new Error('No invoice id');
      const [invoice, lines] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoiceId).single(),
        supabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', invoiceId)
          .order('position', { ascending: true }),
      ]);
      if (invoice.error) throw invoice.error;
      if (lines.error) throw lines.error;
      return {
        ...(invoice.data as InvoiceRow),
        lines: (lines.data ?? []) as InvoiceLineRow[],
      };
    },
    staleTime: 5_000,
  });
}

export interface CreateInvoiceDraftInput {
  clinic_id: string;
  patient_id: string;
  appointment_id?: string;
  due_at?: string;
  notes?: string;
}

function embeddedIsBlocker(
  service: { is_blocker: boolean } | { is_blocker: boolean }[] | null,
): boolean {
  if (!service) return false;
  if (Array.isArray(service)) return Boolean(service[0]?.is_blocker);
  return Boolean(service.is_blocker);
}

export function useCreateInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvoiceDraftInput): Promise<InvoiceRow> => {
      if (input.appointment_id) {
        const { data: row, error: blockError } = await supabase
          .from('appointments')
          .select('patient_id, service:services!appointments_service_id_fkey(is_blocker)')
          .eq('id', input.appointment_id)
          .maybeSingle();
        if (!blockError && row) {
          const invoiceable = appointmentRowIsInvoiceable({
            patientId: row.patient_id,
            isBlocker: embeddedIsBlocker(
              row.service as { is_blocker: boolean } | { is_blocker: boolean }[] | null,
            ),
          });
          if (!invoiceable) {
            throw new Error('block_not_invoiceable');
          }
        }
      }
      const { data, error } = await supabase.rpc('create_invoice_draft', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id,
        p_appointment_id: input.appointment_id,
        p_due_at: input.due_at,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'patient', row.patient_id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'uninvoiced-appointments'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient', row.patient_id] });
    },
  });
}

export interface UpdateInvoiceDraftInput {
  invoice_id: string;
  due_at?: string;
  notes?: string;
}

export function useUpdateInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateInvoiceDraftInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('update_invoice_draft', {
        p_invoice_id: input.invoice_id,
        p_due_at: input.due_at,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface AddInvoiceLineInput {
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price_cents: number;
  vat_rate_percent?: number;
  discount_pct?: number;
  discount_amount_cents?: number;
  source?: InvoiceLineSource;
  source_id?: string;
}

export function useAddInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddInvoiceLineInput): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('add_invoice_line', {
        p_invoice_id: input.invoice_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_discount_pct: input.discount_pct,
        p_discount_amount_cents: input.discount_amount_cents,
        p_source: input.source,
        p_source_id: input.source_id,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'uninvoiced-appointments'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient'] });
    },
  });
}

export interface UpdateInvoiceLineInput {
  line_id: string;
  invoice_id: string;
  description?: string;
  quantity?: number;
  unit_price_cents?: number;
  vat_rate_percent?: number;
  discount_pct?: number;
  discount_amount_cents?: number;
  clear_discount?: boolean;
}

export function useUpdateInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateInvoiceLineInput): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('update_invoice_line', {
        p_line_id: input.line_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_discount_pct: input.discount_pct,
        p_discount_amount_cents: input.discount_amount_cents,
        p_clear_discount: input.clear_discount,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (_row, vars) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
    },
  });
}

export interface RemoveInvoiceLineInput {
  line_id: string;
  invoice_id: string;
}

export function useRemoveInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveInvoiceLineInput): Promise<boolean> => {
      const { data, error } = await supabase.rpc('remove_invoice_line', {
        p_line_id: input.line_id,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: (_ok, vars) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'uninvoiced-appointments'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient'] });
    },
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('issue_invoice', {
        p_invoice_id: invoiceId,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface MarkInvoicePaidInput {
  invoice_id: string;
  paid_at?: string;
  payment_method?: string;
  payment_reference?: string;
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkInvoicePaidInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('mark_invoice_paid', {
        p_invoice_id: input.invoice_id,
        p_paid_at: input.paid_at,
        p_payment_method: input.payment_method,
        p_payment_reference: input.payment_reference,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['appointments'] });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface RevertInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useRevertInvoiceToIssued() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RevertInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('revert_invoice_to_issued', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(row.id) });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface VoidInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VoidInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('void_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export function formatMoney(cents: number, currency: string = 'DKK'): string {
  return (cents / 100).toLocaleString('da-DK', {
    style: 'currency',
    currency,
  });
}

// ── MOD-7b: Invoice emails (PDF + email send) ─────────────────────────────────

export interface InvoiceEmailRow {
  id: string;
  clinic_id: string;
  invoice_id: string;
  sent_to: string;
  sent_by: string | null;
  postmark_message_id: string | null;
  status: 'sent' | 'failed';
  error_message: string | null;
  pdf_storage_path: string;
  subject: string;
  created_at: string;
}

export const invoiceEmailsQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'emails', invoiceId] as const;

export function useInvoiceEmails(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceEmailsQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceEmailRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_emails')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceEmailRow[];
    },
    staleTime: 10_000,
  });
}

export interface RenderInvoicePdfOutput {
  storage_path: string;
  signed_url: string;
}

export function useRenderInvoicePdf() {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<RenderInvoicePdfOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RenderInvoicePdfOutput;
      }>('render-invoice-pdf', { body: { invoice_id: invoiceId } });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('PDF render failed');
      return data.data;
    },
  });
}

export interface SendInvoiceEmailInput {
  invoice_id: string;
  recipient?: string;
  subject?: string;
  message?: string;
}

export interface SendInvoiceEmailOutput {
  invoice_email_id: string;
  postmark_message_id: string | null;
}

export function useSendInvoiceEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendInvoiceEmailInput): Promise<SendInvoiceEmailOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: SendInvoiceEmailOutput;
      }>('send-invoice-email', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('Send email failed');
      return data.data;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: invoiceEmailsQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['payment-requests', vars.invoice_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['email-history'] });
      invalidateAppointmentInvoiceViews(qc);
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'patient'] });
    },
  });
}

// ── MOD-7c: Invoice payment tracking ─────────────────────────────────────────

export const invoicePaymentsQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'payments', invoiceId] as const;

export function useInvoicePayments(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoicePaymentsQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoicePaymentRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoicePaymentRow[];
    },
    staleTime: 10_000,
  });
}

export interface RecordPaymentInput {
  invoice_id: string;
  amount_cents: number;
  payment_date?: string;
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPaymentInput): Promise<InvoicePaymentRow> => {
      const { data, error } = await supabase.rpc('record_invoice_payment', {
        p_invoice_id: input.invoice_id,
        p_amount_cents: input.amount_cents,
        p_payment_date: input.payment_date,
        p_payment_method: input.payment_method,
        p_payment_reference: input.payment_reference,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoicePaymentRow;
    },
    onSuccess: (payment) => {
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(payment.invoice_id) });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(payment.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface CancelInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CancelInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('cancel_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

export interface RefundInvoiceInput {
  invoice_id: string;
  reason: string;
  refund_amount_cents?: number;
}

export function useRefundInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RefundInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('refund_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
        p_refund_amount_cents: input.refund_amount_cents,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'credit-notes', row.id] });
      invalidateAppointmentInvoiceViews(qc);
    },
  });
}

// ── MOD-7d: Invoice reminder system ─────────────────────────────────────────

export interface InvoiceReminderRow {
  id: string;
  clinic_id: string;
  invoice_id: string;
  reminder_number: number;
  sent_to: string;
  sent_by: string | null;
  postmark_message_id: string | null;
  status: 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  subject: string;
  days_overdue: number;
  is_manual: boolean;
  created_at: string;
}

export const invoiceRemindersQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'reminders', invoiceId] as const;

export const invoiceReminderLevelsQueryKey = (invoiceIds: string[]) =>
  ['invoices', 'reminder-levels', [...invoiceIds].sort()] as const;

export function useInvoiceReminders(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceRemindersQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceReminderRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_reminders')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceReminderRow[];
    },
    staleTime: 10_000,
  });
}

export function useInvoiceReminderLevels(invoiceIds: string[]) {
  return useQuery({
    queryKey: invoiceReminderLevelsQueryKey(invoiceIds),
    enabled: invoiceIds.length > 0,
    queryFn: async (): Promise<Record<string, number>> => {
      if (invoiceIds.length === 0) return {};
      const { data, error } = await supabase
        .from('invoice_reminders')
        .select('invoice_id, reminder_number, status')
        .in('invoice_id', invoiceIds)
        .eq('status', 'sent');
      if (error) throw error;
      return reminderLevelsByInvoiceId((data ?? []) as InvoiceReminderLevelSource[]);
    },
    staleTime: 15_000,
  });
}

export interface SendInvoiceReminderInput {
  invoice_id: string;
  custom_message?: string;
}

export interface SendInvoiceReminderOutput {
  processed_clinics: number;
  overdue_invoices: number;
  reminders_sent: number;
  reminders_skipped: number;
  errors: number;
}

export function useSendInvoiceReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendInvoiceReminderInput): Promise<SendInvoiceReminderOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: SendInvoiceReminderOutput;
      }>('send-invoice-reminders', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('Send reminder failed');
      return data.data;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: invoiceRemindersQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'reminder-levels'] });
      qc.invalidateQueries({ queryKey: ['email-history'] });
    },
  });
}
